import { resolveEloHeight, type C1_EloPosition } from "./C1_EloHeights";
import { c1_state } from "./C1_runtimeContext";
import { migrateConsumerDetails } from "../../../src/models/consumerChannelMigration";
import {
  type C1_EloItemInstanceData,
  type C1_LegacyEloItemTreeData,
  type C1_InteractiveEloPlacement,
} from "../types/C1_EloTypes";
import { C1_BaseObject2 } from "./C1_BaseObject2";
import { resolveEloDefinitionPath } from "./C1_eloDefinitionPath";
import { evalExpression } from "../lib/C1_evalExpression";
import type BaseModel from "../../../src/models/BaseModel";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { C1_EloSpatialEvaluator, type C1_EloFormulaError } from "./C1_EloSpatialEvaluator";
import type { C1_Room } from "./C1_Room";
import { resolveInteractiveEloPosition, moveInteractiveEloPlacement } from "./C1_interactiveEloPlacement";
import { isSingleLightDefinitionId, syncSingleLight } from "../furniture/C1_lightGroup";

/** Returns whether persisted input still uses the former nested ELO data tree. */
function isLegacyEloItemData(
  data: C1_EloItemInstanceData | C1_LegacyEloItemTreeData,
): data is C1_LegacyEloItemTreeData {
  return "definitionId" in data || "items" in data || "id" in data;
}

/**
 * Converts one former ELO tree node into facet-only instance data.
 *
 * Structural fields (`id`, `definitionId`, `items`, `name`, `tag`) belong to
 * ESTADA or the definition model and are not copied. The old definition key is
 * resolved once to the expanded registry path. A repetition suffix becomes
 * the explicit instance value `repeatIndex`.
 */
export function migrateLegacyEloItemTreeNode(
  input: C1_EloItemInstanceData | C1_LegacyEloItemTreeData,
  owner: BaseModel,
  explicitParentDefinitionPath?: string,
): C1_EloItemInstanceData {
  if (!isLegacyEloItemData(input)) return { ...input, _defId: input._defId ? resolveEloDefinitionPath(input._defId) : input._defId };

  let baseDefinitionId = input.definitionId;
  let repeatIndex: number | undefined;
  const repeatMatch = baseDefinitionId.match(/^(.*)-(\d+)$/);
  const ownerParentFacet = owner.parent?.c1;
  const parentDefinitionPath =
    explicitParentDefinitionPath ?? (ownerParentFacet instanceof C1_EloItem ? ownerParentFacet._defId : "");
  let definitionPath = resolveEloDefinitionPath(parentDefinitionPath ? `${parentDefinitionPath}/${baseDefinitionId}` : baseDefinitionId);

  if (!state.objectRegistry.getObject<C1_DefinitionItem>(definitionPath) && repeatMatch) {
    baseDefinitionId = repeatMatch[1];
    repeatIndex = Number(repeatMatch[2]);
    definitionPath = parentDefinitionPath ? `${parentDefinitionPath}/${baseDefinitionId}` : baseDefinitionId;
  }

  return {
    _defId: definitionPath,
    interactivCreated: input.interactivCreated,
    interactivePlacement: input.interactivePlacement,
    masterIndex: input.masterIndex,
    position: input.position ? { ...input.position } : undefined,
    count: input.count,
    repeatIndex,
    details: input.details ? migrateConsumerDetails({ ...input.details }) : undefined,
    properties: input.properties ? { ...input.properties } : undefined,
  };
}

/**
 * Sucht zuerst nach der exakten Definition. Wiederholte Items tragen IDs wie
 * `plug.generic-1`; für sie wird ersatzweise `plug.generic` aufgelöst.
 */
export default class C1_EloItem extends C1_BaseObject2 {
  /** Absolute plan coordinates for this render only; never part of the persisted facet data. */
  temporaryDisplayPosition?: { x: number; y: number };

  get displayPosition(): C1_EloPosition | undefined {
    const point = this.temporaryDisplayPosition ?? this.evaluatedAbsolutePosition;
    return point ? { ...point, z: this.evaluatedZ } : undefined;
  }

  /** Mounting heights are absolute above the floor, never summed through parents. */
  get evaluatedZ(): number {
    const manual = this.position?.z ?? this.data.interactivePlacement?.relativePosition.z;
    if (manual !== undefined) return resolveEloHeight(manual, c1_state.settings?.eloHeights);
    const parent = this.parent;
    if (this.definition?.type === "plug" && parent instanceof C1_EloItem && parent.definition?.type === "plug_group") {
      return parent.evaluatedZ;
    }
    const z = this.definition?.type === "button" && this.data.details?.buttonType === "panel" ? -7 : this.definition?.position?.z;
    return resolveEloHeight(z, c1_state.settings?.eloHeights);
  }

  /** Shared by groups, consumers and buttons; product data is stored only on the definition. */
  get material() {
    return this.definition?.material;
  }

  get className(): string {
    return "C1_EloItem";
  }

  constructor(data: C1_EloItemInstanceData, owner: BaseModel) {
    super(data, owner);
    // ELO-Tags werden direkt im kanonischen, persistierten Instanzbaum erzeugt.
    // `super` legt zunächst eine Kopie an; die ursprüngliche Referenz wird hier
    // wiederhergestellt, damit spätere Tag-Zuweisungen sofort sichtbar sind.
  }

  /**
   * Resolves the facet's definition without owning or copying it.
   * Undefined means the persisted reference is empty or no longer resolvable.
   */
  /** Declares the complete facet payload and its definition reference as persistent. */
  /** Serializes all editable instance values, including the definition reference. */
  /** Restores instance values and synchronizes the runtime definition reference. */
  /**
   * Creates a detached node for the remaining legacy tree editors.
   *
   * This adapter is deliberately one-way: the facet itself never owns or
   * persists the returned `id`, `definitionId` or `items` fields.
   */
  toLegacyTreeData(): C1_LegacyEloItemTreeData {
    const baseDefinitionId = this.definition?.definitionId ?? "";
    const definitionId =
      this.data.repeatIndex === undefined
        ? baseDefinitionId
        : `${baseDefinitionId}-${this.data.repeatIndex}`;
    return {
      id: this.owner?.id ?? "",
      interactivCreated: this.data.interactivCreated,
      interactivePlacement: this.data.interactivePlacement,
      definitionId,
      name: this.definition?.defaultName === "" ? this.owner?._name ?? "" : (this.owner?.name ?? ""),
      tag: this.owner?.tag,
      masterIndex: this.data.masterIndex,
      position: this.data.position ? { ...this.data.position } : undefined,
      count: this.data.count,
      details: this.data.details ? { ...this.data.details } : undefined,
      properties: this.data.properties ? { ...this.data.properties } : undefined,
      items: [],
    };
  }

  // Direktes Ergebnis der x-/y-Formeln aus der Definition. Die Position ist
  // lokal zum Eltern-Item und enthält noch keine aufsummierten Elternwege.
  readonly spatialEvaluation = new C1_EloSpatialEvaluator(this);

  get evaluatedDefinitionPosition(): { x: number; y: number } | undefined {
    return this.spatialEvaluation.evaluatedDefinitionPosition;
  }

  private set evaluatedDefinitionPosition(value: { x: number; y: number } | undefined) {
    this.spatialEvaluation.evaluatedDefinitionPosition = value;
  }

  // Relative Position im Koordinatensystem des besitzenden Objekts. Hier sind
  // die ELO-Elternpositionen bereits enthalten, Möbelrotation und Ursprung noch nicht.
  get evaluatedPosition(): { x: number; y: number } | undefined {
    return this.spatialEvaluation.evaluatedPosition;
  }

  private set evaluatedPosition(value: { x: number; y: number } | undefined) {
    this.spatialEvaluation.evaluatedPosition = value;
  }

  // Finale Planposition nach Spiegelung, Rotation und Verschiebung.
  get evaluatedAbsolutePosition(): { x: number; y: number } | undefined {
    return this.spatialEvaluation.evaluatedAbsolutePosition;
  }

  private set evaluatedAbsolutePosition(value: { x: number; y: number } | undefined) {
    this.spatialEvaluation.evaluatedAbsolutePosition = value;
  }

  // Die Modelobjekte der Kinder werden erst beim ersten Zugriff erzeugt.

  /** Manuell gesetzte Position in Metern relativ zum Ursprung des Besitzers. */
  get position(): C1_EloPosition | undefined {
    return this.data.position;
  }

  get interactivCreated(): boolean | undefined {
    return this.data.interactivCreated;
  }

  set interactivCreated(value: boolean | undefined) {
    if (this.data.interactivCreated === value) return;
    this.beforeChange("interactivCreated");
    this._data.interactivCreated = value;
    this.changed("interactivCreated");
  }

  /** Resolves the direct room owner without introducing a furniture wrapper. */
  get interactiveRoom(): C1_Room | undefined {
    const parent = this.owner?.parent;
    return this.interactivCreated && parent?.className === "Room" ? parent.c1 as C1_Room : undefined;
  }

  get independentAbsolutePosition(): { x: number; y: number } | undefined {
    const room = this.interactiveRoom;
    const placement = this.data.interactivePlacement;
    return resolveInteractiveEloPosition(placement, room?.innerRect());
  }

  /** Persists docking on the device facet and updates light mounting without replacing the consumer. */
  setInteractivePlacement(placement: C1_InteractiveEloPlacement): void {
    const updateLightName = isSingleLightDefinitionId(placement.paletteId) && !this.owner?.hasCustomDisplayName();
    this.beforeChange("interactivePlacement");
    this._data.interactivePlacement = {
      ...placement,
      relativePosition: { ...placement.relativePosition },
      _wallId: placement.side ? placement._wallId ?? this.interactiveRoom?.wallForSide(placement.side)?.id : undefined,
    };
    if (isSingleLightDefinitionId(placement.paletteId)) {
      const tree = { id: "", definitionId: placement.paletteId, items: [this.toLegacyTreeData()] };
      const synchronized = syncSingleLight({ width: 0.16, height: 0.16, rotation: placement.rotation }, tree,
        placement.wallDocked !== undefined, placement.paletteId);
      const light = synchronized.eloTree.items[0];
      const definitionId = `${placement.paletteId}/${light.definitionId}`;
      if (state.objectRegistry.getObject(definitionId)) {
        this._defId = definitionId;
        this._data._defId = definitionId;
        this._data.details = light.details;
        if (updateLightName && this.owner) this.owner.name = this.definition?.description[this.language] ?? this.owner.name;
      }
    }
    this.changed("interactivePlacement");
  }

  /** Keeps the device and its connections while turning an owner child into a room placement. */
  adoptInteractiveRoom(room: C1_Room, definition: C1_DefinitionItem, placement: C1_InteractiveEloPlacement): boolean {
    const owner = this.owner as BaseModel | undefined;
    if (!room.owner || !owner?.moveToNewParent(room.owner as BaseModel)) return false;
    this.beforeChange();
    this._data = { ...this._data, position: undefined, repeatIndex: undefined, masterIndex: undefined };
    this.definition = definition;
    this.interactivCreated = true;
    this.setInteractivePlacement(placement);
    (this.owner && (this.owner.name = this.definition?.description[this.language] ?? ""));
    this.setTag();
    this.changed();
    return true;
  }

  /** Moves a standalone item with the same wall snapping used by the creation preview. */
  moveInteractiveTo(point: { x: number; y: number }, disableDocking = false): void {
    const room = this.interactiveRoom;
    const placement = this.data.interactivePlacement;
    if (!room || !placement) return;
    this.setInteractivePlacement(moveInteractiveEloPlacement(placement, point, room.innerRect(), [], disableDocking, room.placementGeometry()));
  }

  setPosition(position: C1_EloPosition | undefined): void {
    if (this.position?.x === position?.x && this.position?.y === position?.y && this.position?.z === position?.z) return;
    this.beforeChange("position");
    this._data.position = position ? { ...position } : undefined;
    this.changed("position");
  }

  protected get placementChildren(): C1_EloItem[] {
    // if (!this._items) {
    //   // Die Parent-Verknüpfung wird sowohl für Namensvorlagen als auch für die
    //   // rekursive Positionsberechnung benötigt.
    //   this._items = this.data.items.map((item) => new C1_EloItem(item, this));
    // }
    // return this._items;
    return [];
  }

  /** Children participating in spatial evaluation. */
  get spatialChildren(): readonly C1_EloItem[] {
    return this.placementChildren;
  }

  private legacyAllItemsRemoved(): C1_EloItem[] {
    // Preorder: aktuelles Item zuerst, anschließend rekursiv alle Kinder.
    return [this];
  }

  private legacyFindItemRemoved(id: string): C1_EloItem | undefined {
    // Objekt-IDs sind projektweit eindeutig, daher genügt der erste Treffer.
    return this.owner?.id === id ? this : undefined;
  }

  /**
   * Wertet zuerst die lokalen Definitionspositionen aus und transformiert dann
   * den vollständigen Baum in Plan-Koordinaten. Renderer und Export verwenden
   * denselben Einstiegspunkt und damit identische Platzierungsregeln.
   */
  evaluateAbsolutePlacements(
    origin: { x: number; y: number },
    rotationInDegree = 0,
    reflect = false,
    props: Record<string, number> = {},
    onFormulaError?: (error: C1_EloFormulaError) => void,
  ) {
    // Phase 1: Formeln auswerten und relative Elternwege aufsummieren.
    this.spatialEvaluation.evaluateAbsolutePlacements(origin, rotationInDegree, reflect, props, onFormulaError);
    // Phase 2: Spiegelung, Rotation und Ursprung des Möbelstücks anwenden.
    return this;
  }

  calcAbsolutePositions(originX: number, originY: number, rotationInDegree: number, reflect?: boolean) {
    this.spatialEvaluation.calcAbsolutePositions(originX, originY, rotationInDegree, reflect);
    return;
    // Die trigonometrischen JavaScript-Funktionen erwarten Radiant.
    const rad = (rotationInDegree * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // Spiegelung erfolgt vor der Rotation an der lokalen y-Achse.
    const r = reflect ? -1 : 1;

    const evaluatedPosition = this.evaluatedPosition;
    if (evaluatedPosition) {
      const x = evaluatedPosition!.x * r;
      const y = evaluatedPosition!.y;
      this.evaluatedAbsolutePosition = {
        // Zweidimensionale Rotationsmatrix plus Ursprung im Raumplan.
        x: originX + x * cos - y * sin,
        y: originY + x * sin + y * cos,
      };
    }

    // Die Kinder enthalten bereits die aufsummierte relative Position und
    // erhalten daher exakt dieselbe globale Transformation.
    for (const item of this.placementChildren) {
      item.calcAbsolutePositions(originX, originY, rotationInDegree, reflect);
    }
  }

  evalRelativePlacements(
    props: Record<string, number> = {},
    parentPosition = { x: 0, y: 0 },
    parentRepeatIndex?: number,
    onFormulaError?: (error: C1_EloFormulaError) => void,
    siblingIndex?: number,
    siblingCount?: number,
  ) {
    const eloEquipmentDefinition = this.definition;

    // Formeln arbeiten nullbasiert, die persistierten Wiederholungsendungen
    // beginnen dagegen bei 1. Kinder erben den Index ihrer Wiederholungsgruppe.
    const repeatIndex = this.data.repeatIndex === undefined ? parentRepeatIndex : this.data.repeatIndex - 1;

    // Das Vorzeichen der Elternposition erlaubt symmetrische Formeln für linke
    // und rechte Seiten, ohne zwei nahezu identische Definitionen zu benötigen.
    const signX = Math.sign(parentPosition.x);
    const signY = Math.sign(parentPosition.y);
    const context: Record<string, number> = {
      ...props,
      // SignX/SignY bleiben als Aliasse für bestehende Katalogformeln erhalten.
      SignX: signX,
      SignY: signY,
      SignGX: signX,
      SignGY: signY,
      // I ist der Wiederholungsindex. ItemIndex und ItemCount beschreiben die
      // Position in der aktuellen Geschwisterliste.
      ...(repeatIndex === undefined ? {} : { I: repeatIndex }),
      ...(siblingIndex === undefined ? {} : { ItemIndex: siblingIndex }),
      ...(siblingCount === undefined ? {} : { ItemCount: siblingCount }),
    };
    const x = eloEquipmentDefinition?.position?.x || 0;
    const y = eloEquipmentDefinition?.position?.y || 0;

    const evaluate = (value: number | string, axis: "x" | "y") => {
      // Reine Zahlen benötigen keine Auswertung durch den Ausdrucksparser.
      if (typeof value !== "string") return value;
      try {
        return evalExpression(value, context);
      } catch (error) {
        // Eine fehlerhafte Katalogformel darf den restlichen Plan nicht stoppen.
        // Sie wird gemeldet und die betroffene Achse fällt kontrolliert auf 0 zurück.
        onFormulaError?.({
          itemId: this.owner?.id ?? "",
          definitionId: eloEquipmentDefinition?.definitionId ?? "",
          axis,
          expression: value,
          message: error instanceof Error ? error.message : String(error),
        });
        return 0;
      }
    };
    const evaluatedX = evaluate(x, "x");
    const evaluatedY = evaluate(y, "y");

    // Beide Stufen bleiben erhalten: die lokale Formelposition für Diagnose und
    // die aufsummierte Position als Grundlage der globalen Transformation.
    this.evaluatedDefinitionPosition = { x: evaluatedX, y: evaluatedY };
    this.evaluatedPosition = this.position
      ? { ...this.position }
      : {
          x: parentPosition.x + evaluatedX,
          y: parentPosition.y + evaluatedY,
        };

    // Neben dem Wiederholungsindex werden Geschwisterposition und -anzahl
    // weitergereicht, damit Kindformeln Elemente gleichmäßig verteilen können.
    const children = this.placementChildren;
    for (let itemIndex = 0; itemIndex < children.length; itemIndex += 1) {
      const subItem = children[itemIndex];
      subItem.evalRelativePlacements(
        props,
        this.evaluatedPosition,
        repeatIndex,
        onFormulaError,
        itemIndex,
        children.length,
      );
    }
  }
}
